/**
 * AgentModelPicker — the provider + model selection pair for the agent editor,
 * extracted from AgentEditorView so every state
 * is pure and Storybook-reachable.
 *
 * Contract:
 *  - Shows the SAVED provider/model as the selected values — never "first of
 *    list". The parent initializes from the saved config; this component only
 *    renders what it is given.
 *  - When the current `model` is not in `models` (e.g. an LM Studio model on a
 *    web client that hasn't discovered it), it renders the saved value as a
 *    distinct "unavailable in this catalog view" option WITHOUT rewriting it.
 *    Saving untouched keeps the saved value; explicitly choosing a listed model
 *    replaces it.
 *  - After a provider change the parent blanks `model` to '' — this renders a
 *    "Choose a model…" placeholder and the parent blocks save until a model is
 *    chosen (fail-visible, no fabricated choice).
 */

import type { ModelLifecycleStatus } from '@localagent/sdk';

export interface AgentModelPickerProps {
  /** All selectable providers. */
  providers: string[];
  /** Models available for the CURRENT provider (from models_by_provider). */
  models: string[];
  /** Currently selected provider. */
  provider: string;
  /** Currently selected model name ('' = none chosen yet → placeholder). */
  model: string;
  onProviderChange: (provider: string) => void;
  onModelChange: (model: string) => void;
  disabled?: boolean;
  /** Optional label formatter for provider names (defaults to identity). */
  formatProviderLabel?: (provider: string) => string;
  /**
   * Lifecycle status per model name, as the server DERIVED it — the picker never
   * computes it from a date. A model absent from the map, or
   * marked `active`, renders with no suffix.
   *
   * A `<select>` cannot carry a badge, so the suffix is the whole affordance
   * here; the dated warning belongs in the editor. Nothing blocks:
   * both `deprecated` and `retired` remain selectable (never break a
   * running agent).
   */
  modelStatuses?: Record<string, ModelLifecycleStatus>;
}

export function AgentModelPicker({
  providers,
  models,
  provider,
  model,
  onProviderChange,
  onModelChange,
  disabled,
  formatProviderLabel,
  modelStatuses,
}: AgentModelPickerProps) {
  const labelFor = formatProviderLabel ?? ((p: string) => p);

  // A non-empty saved model that isn't in the current provider's option list —
  // shown explicitly rather than silently rewritten.
  const savedModelUnavailable = model !== '' && !models.includes(model);
  // No model chosen yet (fresh provider change) — the parent blocks save until
  // one is picked.
  const needsModelChoice = model === '';

  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="mb-2 block text-label font-medium">Provider</label>
        <select
          data-testid="agent-provider-select"
          value={provider}
          onChange={e => onProviderChange(e.target.value)}
          disabled={disabled}
          className="w-full rounded-md bg-muted px-3 py-2 text-label focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
        >
          {providers.map(p => (
            <option key={p} value={p}>
              {labelFor(p)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-2 block text-label font-medium">Model</label>
        <select
          data-testid="agent-model-input"
          value={model}
          onChange={e => onModelChange(e.target.value)}
          disabled={disabled}
          className="w-full rounded-md bg-muted px-3 py-2 text-label focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
        >
          {needsModelChoice && (
            // Placeholder shown after a provider change until the user picks a
            // model. Kept selectable-as-current but the parent blocks save.
            <option value="">Choose a model…</option>
          )}
          {savedModelUnavailable && (
            // The saved value stays selected and is NOT rewritten; the suffix +
            // hint below explain it isn't in this client's catalog view.
            <option value={model}>{model} (unavailable here)</option>
          )}
          {models.map(m => {
            const status = modelStatuses?.[m];
            const suffix =
              status === 'retired'
                ? ' (retired)'
                : status === 'deprecated'
                  ? ' (deprecated)'
                  : '';
            return (
              <option key={m} value={m} data-status={status ?? 'active'}>
                {m}
                {suffix}
              </option>
            );
          })}
        </select>
        {savedModelUnavailable && (
          <p
            data-testid="agent-model-unavailable-hint"
            className="mt-1 text-caption text-warning-text"
          >
            &ldquo;{model}&rdquo; isn&apos;t in this client&apos;s model list (e.g.
            an LM Studio model discovered on another device). It is kept as-is;
            pick a listed model to change it.
          </p>
        )}
        {needsModelChoice && (
          <p
            data-testid="agent-model-choose-hint"
            className="mt-1 text-caption text-muted-foreground"
          >
            Choose a model to continue.
          </p>
        )}
      </div>
    </div>
  );
}
